import { useEffect, useId, useRef } from 'react';
import { useFocusTrap } from '../../hooks/useFocusTrap';
import { Z } from './zLayers';
import { openGlobalOverlay, closeGlobalOverlay } from '../../util/globalOverlays.js';

// T131 — shared dialog primitive. Generalises the ConfirmDialog scaffold (the
// one overlay already wired with useFocusTrap): backdrop + panel + focus trap +
// gated Escape/backdrop close + a single z-index ladder. Rendered INLINE (no
// portal — matches every existing test that queries the live DOM).
//
// Slots are optional: pass `title`/`eyebrow`/`footer` for the common case, or
// pass everything as `children` (bare mode) for overlays with bespoke headers —
// then set `labelledById` (an existing <h2 id>) or `ariaLabel` so the dialog
// keeps an accessible name.
//
// escapeClosesOnTop=false suppresses Escape (used while a nested ConfirmDialog
// is open, so one keypress closes only the child — the T110/T111 contract).

const SIZE = { sm: 'max-w-md', md: 'max-w-2xl', lg: 'max-w-3xl', xl: 'max-w-4xl' };

export default function Modal({
  open = true,
  onClose,
  title,
  eyebrow,
  footer,
  children,
  size = 'md',
  scroll = false,
  tone = 'modal', // 'modal' → z-50 · 'over' → z-60 (ConfirmDialog / KeyboardHelp)
  role = 'dialog',
  placement = 'sheet', // 'sheet' = bottom-sheet < sm · 'center' = always centered
  showClose = true,
  escapeClosesOnTop = true,
  initialFocusRef,
  labelledById,
  ariaLabel,
  scrimClassName = 'bg-black/60',
}) {
  const panelRef = useRef(null);
  const autoId = useId();
  const titleId = labelledById ?? `${autoId}-title`;
  useFocusTrap(panelRef, open, initialFocusRef);

  // T476 — every modal announces itself on the global channel.
  //
  // `globalOverlays.js` exists because an overlay mounted OUTSIDE the page
  // tree is invisible to the page (T290b, written for the command palette).
  // The four artefact modals are inside the page today, so each host gathers
  // them by hand into a `childModalOpen` expression built from ITS OWN state
  // — and that expression is what gates the triage keyboard, where `d`
  // dismisses a row and is not undoable without finding the row again.
  //
  // T422 moves those modals into a shell. The moment they leave the page,
  // every per-page `childModalOpen` stops seeing them and the keyboard
  // re-arms underneath an open dialog. Announcing here, in the one primitive
  // all nine modals already use, means the guard keeps working wherever the
  // modal is mounted — and it is true today as well, so this is testable now
  // rather than at the move.
  //
  // Deliberately NOT done in `ui/Drawer`: the drawer reads this flag to know
  // whether something sits above IT, and a drawer that announced itself would
  // conclude it was covered by its own dialog and stop closing on Escape.
  useEffect(() => {
    if (!open) return undefined;
    openGlobalOverlay(autoId);
    return () => closeGlobalOverlay(autoId);
  }, [open, autoId]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape' && escapeClosesOnTop) onClose?.();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose, escapeClosesOnTop]);

  if (!open) return null;

  const z = tone === 'over' ? Z.dialog : Z.modal;
  const items = placement === 'sheet' ? 'items-end sm:items-center' : 'items-center';
  const pad = placement === 'sheet' ? 'p-0 sm:p-4' : 'p-4';
  const radius = placement === 'sheet' ? 'rounded-t-2xl sm:rounded-2xl' : 'rounded-2xl';
  const hasHeader = !!(title || eyebrow);

  return (
    <div
      role={role}
      aria-modal="true"
      aria-labelledby={title ? titleId : labelledById}
      aria-label={ariaLabel}
      className={`fixed inset-0 ${z} flex ${items} justify-center ${scrimClassName} ${pad}`}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose?.();
      }}
    >
      <div
        ref={panelRef}
        tabIndex={-1}
        className={
          `flex w-full ${SIZE[size]} flex-col overflow-hidden border border-slate-800 bg-slate-900 shadow-2xl outline-none ${radius} ` +
          (scroll ? 'h-full max-h-[95vh]' : '')
        }
      >
        {hasHeader && (
          <header className="flex items-start justify-between gap-3 border-b border-slate-800 p-4">
            <div className="min-w-0">
              {eyebrow && <p className="text-[10px] uppercase tracking-wide text-brand-500">{eyebrow}</p>}
              {title && (
                <h2 id={titleId} className="mt-0.5 truncate text-base font-semibold text-slate-100">
                  {title}
                </h2>
              )}
            </div>
            {showClose && onClose && (
              <button
                type="button"
                onClick={onClose}
                aria-label="Close"
                className="shrink-0 rounded-md border border-slate-700 px-2 py-1 text-xs text-slate-300 hover:border-slate-500 hover:text-slate-200"
              >
                ✕
              </button>
            )}
          </header>
        )}
        {/* Slot layout wraps the body in the scroll region; bare-children modals
            (their own fixed header + a flex-1 scrolling body) just get a
            height-constrained panel and manage their own overflow. */}
        {scroll && (hasHeader || footer) ? (
          <div className="flex-1 overflow-y-auto">{children}</div>
        ) : (
          children
        )}
        {footer && (
          <footer className="flex flex-wrap items-center gap-2 border-t border-slate-800 bg-slate-900/60 p-3">
            {footer}
          </footer>
        )}
      </div>
    </div>
  );
}
